import { HTMLAttributes, useMemo } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Section from '@/components/base/Section'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type ImageText = HTMLAttributes<HTMLElement> &
  Partial<{
    image: WPImage
    overline: string
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    title: string
    paragraph: string
    button: WPLink
  }>

const ImageText = ({
  overline,
  title,
  title_variant,
  title_component,
  image,
  paragraph,
  button,
  className,
  ...props
}: ImageText) => {
  const ratio = useMemo(() => {
    const width = image?.width || 0
    const height = image?.height || 0
    return `${(height / width) * 100}%`
  }, [image])

  return (
    <Section className={cn([className])} {...props}>
      <Container>
        <Grid className="gap-y-[2rem]">
          <div className="col-span-4 md:col-span-3 lg:col-span-6">
            <div className="grid gap-y-[1rem] md:gap-y-[1.5rem]">
              <div
                className="relative h-0 w-full rounded-[3rem] bg-dark-blue"
                style={{ paddingBottom: ratio }}
              >
                <Image
                  source={image}
                  fill
                  fit="cover"
                  className="rounded-[3rem]"
                />
              </div>
              <div className="flex">
                <Typography variant="small" className="text-mainbody/40">
                  {image?.caption}
                </Typography>
              </div>
            </div>
          </div>
          <div className="col-span-4 md:col-span-3 lg:col-span-5 lg:col-start-8">
            <div className={cn(['grid gap-y-[2rem]'])}>
              {overline ? (
                <Typography variant="h6" className="text-heading">
                  {overline}
                </Typography>
              ) : null}

              {title ? (
                <Typography variant={title_variant} component={title_component}>
                  {title}
                </Typography>
              ) : null}

              {paragraph || button ? (
                <div
                  className={cn([
                    'grid gap-y-[2rem]',
                    'md:max-w-[40rem] lg:max-w-[44rem]'
                  ])}
                >
                  {paragraph ? (
                    <Typography
                      variant="body"
                      paragraph
                      className="text-foreground"
                    >
                      {paragraph}
                    </Typography>
                  ) : null}

                  {button ? (
                    <div className="flex">
                      <Button variant="default" asChild>
                        <Link href={button.url} target={button.target}>
                          <span data-label={button.title}>
                            <Typography variant="label">
                              {button.title}
                            </Typography>
                          </span>
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default ImageText
